import {useInfiniteQuery} from '@tanstack/react-query';
import {useEffect, useMemo, useRef, useState} from 'react';
import {QUERY_KEYS} from '../utils/queryKeys';
import {IElementQuestionAnswer, IQuestionAnswerType} from '../incase-domain/elements/base/questionElementBase';
import {IElementMultiSelect} from '../incase-domain/elements/question/multiSelect';
import {fetchCodebookEntries} from '../service/codebook.service';
import {getNextPage} from '../utils/pagination';

interface IUseCodebookReturn {
    answersRef: React.RefObject<HTMLDivElement>;
    searchAnswers: string;
    setSearchAnswers: React.Dispatch<React.SetStateAction<string>>;
    codebookAnswers: IElementQuestionAnswer[];
    onScrollToBottom: () => void;
    refetch: () => void;
}

export const useCodebook = (codebookId: string, element?: IElementMultiSelect): IUseCodebookReturn => {
    const answersRef = useRef<HTMLDivElement>(null);
    const [searchAnswers, setSearchAnswers] = useState<string>('');
    const {data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch} = useInfiniteQuery(
        QUERY_KEYS.codebook.entries(codebookId, searchAnswers),
        ({pageParam = 1}) => (codebookId ? fetchCodebookEntries(codebookId, pageParam, 20, searchAnswers) : undefined),
        {
            enabled: !!codebookId,
            getNextPageParam: (lastPage) => {
                return getNextPage(lastPage);
            },
        }
    );
    const codebookAnswers = useMemo(() => {
        return (
            data?.pages.flatMap(
                (page) =>
                    page?.content?.map((entry) => ({
                        codebookEntryId: entry._id,
                        text: entry.value,
                        depth: entry.depth || 0,
                        numberOfChildren: entry.numberOfChildren || 0,
                        type: 'incorrect' as IQuestionAnswerType,
                        comment: element?.answers.find((a) => a.text === entry.value)?.comment || '',
                    })) || []
            ) || []
        );
    }, [data, element]);

    const onScrollToBottom = (): void => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    useEffect(() => {
        const answersContainer = answersRef.current;
        if (!answersContainer) return undefined;

        const bottomChild = document.createElement('div');
        bottomChild.style.height = '1px';
        bottomChild.style.width = '100%';
        answersContainer.appendChild(bottomChild);

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                threshold: 0,
                rootMargin: '100px',
            }
        );

        observer.observe(bottomChild);

        return () => {
            observer.disconnect();
            if (answersContainer.contains(bottomChild)) {
                answersContainer.removeChild(bottomChild);
            }
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
    return {answersRef, searchAnswers, setSearchAnswers, codebookAnswers, onScrollToBottom, refetch};
};
